import * as React from 'react'
import { i18n as globalI18n, type I18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { useLingui } from '@lingui/react/macro'
import { useRouterState, type ErrorComponentProps } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { basename, cn } from './lib/utils'
import { withSdkParams } from './lib/sdk-params'
import { resolveRedirectTarget } from './lib/redirect-target'
import { firstIncompleteTestPath, trpc } from './main'
import { classifyRouteError, isChunkLoadError, type RouteErrorUser } from './route-error-classify'
import { isRetryableError } from './lib/query-retry'
import { logFrontendError } from './lib/log-error'
import { reloadForVersionDrift, versionDriftPending } from './lib/app-version'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

// Guard against a reload loop when a chunk is GENUINELY gone rather than unreachable: survives the reload via
// sessionStorage, so after one automatic attempt inside the cooldown we stand down and leave the manual button.
const CHUNK_RELOAD_GUARD_KEY = 'danchiano:chunk-reload-at'
const CHUNK_RELOAD_COOLDOWN_MS = 20_000
function reloadedForChunkRecently(): boolean {
  try {
    const at = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)
    return !!at && Date.now() - Number(at) < CHUNK_RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

// `navigator.onLine` LIES — a down server, a captive portal and DevTools "offline" all still report online — so
// probe with a real, cache-busted same-origin HEAD.
async function serverReachable(): Promise<boolean> {
  try {
    const res = await fetch(`${basename}/favicon.ico?connectivity=${Date.now()}`, { method: 'HEAD', cache: 'no-store' })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Router-wide error boundary (wired as `defaultErrorComponent`). A suspense query THROWS during render, so an
 * error on a streamed query would crash the whole tree (React #418) instead of redirecting; it is caught
 * per-route so the server-rendered chrome stays. The auth and test cases key off the CACHED user rather than the
 * error code, because a deferred-streamed rejection has its message redacted in production. See classifyRouteError.
 */
export function DefaultRouteError(props: ErrorComponentProps & { i18n?: I18n }) {
  // Also the boundary for the ROOT route, where it renders ABOVE AppShell's <I18nProvider> and `useLingui` would
  // throw and blank the whole view (notably an SDK embed) — so it provides its own. Prefer the request-scoped
  // instance passed by the router, which is safe from concurrent SSR renders (React #418).
  return (
    <I18nProvider i18n={props.i18n ?? globalI18n}>
      <RouteErrorContent {...props} />
    </I18nProvider>
  )
}

function RouteErrorContent(props: ErrorComponentProps) {
  const { t } = useLingui()
  const queryClient = useQueryClient()
  const routeId = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId ?? '' })
  // Duck-type rather than `instanceof TRPCClientError`: bundling can give the thrown error a different
  // class identity than the one imported here, so `instanceof` is unreliable across module boundaries.
  const err = props.error as { data?: { code?: string }; message?: string } | undefined
  const message = err?.message
  // ONLY on an auth-required route, so an error on a PUBLIC report never forces an anonymous viewer to log in.
  // getUser is authOptional, so an absent cached user means anonymous or expired.
  const cachedUser = queryClient.getQueryData(trpc.auth.getUser.queryKey()) as RouteErrorUser | null
  // Test-gated reads reject with TEST_INCOMPLETE, but a deferred-streamed rejection is redacted in production — so
  // infer the case from the CACHED user instead of trusting the message. See classifyRouteError.
  const { isAuth, isTestIncomplete } = classifyRouteError({ routeId, cachedUser, message })

  // A transient failure (transport drop, 5xx/408/429) or a failed CODE chunk auto-recovers instead of showing a
  // hard error — and reports itself only when it turns out to be a real defect. See RecoverableErrorNotice.
  const recoverable = isChunkLoadError(props.error) || isRetryableError(props.error)

  // A build deployed mid-session leaves this document rendering payloads shaped for the NEW one, and the drift
  // reload only fires on the next navigation — which the crash preempts (#88). Read once per boundary mount, so
  // a re-render cannot flip the branch out from under the reload.
  const [staleBundle] = React.useState(versionDriftPending)
  React.useEffect(() => {
    if (staleBundle) reloadForVersionDrift()
  }, [staleBundle])

  // Report genuine crashes to the backend so they surface in the server log (CloudWatch/SST) — once
  // per boundary mount. Auth/test cases are normal redirect control-flow, not bugs, so skip them.
  const hasLogged = React.useRef(false)
  React.useEffect(() => {
    if (isAuth || isTestIncomplete || recoverable || staleBundle || hasLogged.current) return
    hasLogged.current = true
    logFrontendError(props.error)
  }, [props.error, isAuth, isTestIncomplete, recoverable, staleBundle])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth) {
      // resolveRedirectTarget unwraps the current URL so an auth error surfacing while already on /login
      // reuses the real destination instead of nesting /login into a fresh (re-encoded) ?redirect=.
      const target = resolveRedirectTarget(window.location.pathname + window.location.search)
      const query = target ? `?redirect=${encodeURIComponent(target)}` : ''
      window.location.replace(withSdkParams(`${basename}/login${query}`))
    } else if (isTestIncomplete && !window.location.pathname.includes('/test')) {
      // Resume at the first unfinished questionnaire, not a bare /test, which does not auto-forward. Carry the API/SDK
      // token-link session params so a token-link visitor is not logged out by the hard redirect.
      window.location.replace(withSdkParams(`${basename}${firstIncompleteTestPath()}`))
    }
  }, [isAuth, isTestIncomplete])

  // Auth/test errors are mid-redirect, and a stale bundle is mid-reload — render nothing rather than flash an error.
  if (isAuth || isTestIncomplete || staleBundle) return null

  if (recoverable) return <RecoverableErrorNotice error={props.error} reset={props.reset} />

  return (
    <div className="flex min-h-[40dvh] w-full flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-foreground text-base font-semibold">{t`Something went wrong`}</p>
      <p className="text-muted-foreground max-w-sm text-sm">{t`We couldn’t load this page. Please try again.`}</p>
      <ButtonPrimitive
        type="button"
        onClick={() => window.location.reload()}
        className={cn('bg-primary text-primary-foreground mt-1 rounded-md px-4 py-2 text-sm font-medium')}
      >
        {t`Try again`}
      </ButtonPrimitive>
    </div>
  )
}

/**
 * Auto-recovery for a failure a retry could actually fix (isRetryableError), which is why the chunk case is
 * detected separately: a DATA error refetches in place, while a CHUNK error needs a full reload, since the
 * browser caches the failed import forever. Either way, wait until the server ANSWERS — reloading blind
 * landed on a dead `chrome-error://` document. Reporting is narrowed to genuine defects, because every
 * report auto-files an issue: an unreachable server is the user's network, and a blip that recovers on the
 * first attempt is a blip. The budget is a module singleton so it survives the remount `reset()` causes.
 */
const AUTO_RETRY_LIMIT = 4
const INCIDENT_RESET_MS = 30_000
let autoRetry = { attempts: 0, lastAt: 0 }

function RecoverableErrorNotice({ error, reset }: { error: unknown; reset: () => void }) {
  const { t } = useLingui()
  const chunkError = isChunkLoadError(error)
  // null = still probing; true/false = the last probe's answer. Drives the copy.
  const [reachable, setReachable] = React.useState<boolean | null>(null)
  // Auto-recovery has given up (chunk gone, or the budget is spent): no spinner and no "we'll keep trying",
  // which would be a lie — just the honest copy and the (always-working) manual button.
  const [standingBy, setStandingBy] = React.useState(false)
  const hasLogged = React.useRef(false)

  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const report = React.useCallback(() => {
    if (hasLogged.current) return
    hasLogged.current = true
    logFrontendError(error)
  }, [error])

  // Only ever called once the server is confirmed reachable.
  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const recover = React.useCallback(
    (manual: boolean) => {
      // A manual click always attempts and resets the budget, so the automatic loop resumes afterwards.
      if (manual) autoRetry = { attempts: 0, lastAt: Date.now() }
      else if (Date.now() - autoRetry.lastAt > INCIDENT_RESET_MS) autoRetry = { attempts: 0, lastAt: Date.now() }

      if (!manual && autoRetry.attempts >= AUTO_RETRY_LIMIT) {
        // Reachable, and it has failed every time we tried: a real backend problem, not a blip.
        setStandingBy(true)
        report()
        return
      }
      autoRetry = { attempts: autoRetry.attempts + 1, lastAt: Date.now() }

      if (!chunkError) {
        reset()
        return
      }
      if (!manual && reloadedForChunkRecently()) {
        // We already spent a reload and the chunk STILL fails with the server up: the asset is genuinely gone.
        setStandingBy(true)
        report()
        return
      }
      try {
        window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()))
      } catch {
        // sessionStorage unavailable (private mode / sandboxed iframe) — reload anyway; worst case is one extra.
      }
      window.location.reload()
    },
    [chunkError, reset, report],
  )

  // Poll the server and recover the instant it answers. Capped backoff (3s → 15s) so a fleet of tabs stranded on
  // one outage doesn't hammer a recovering server, with the `online` event as the fast path.
  React.useEffect(() => {
    if (standingBy) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    let delay = 3000
    const tick = async () => {
      if (cancelled) return
      const ok = await serverReachable()
      if (cancelled) return
      setReachable(ok)
      if (ok) {
        recover(false)
        return
      }
      timer = setTimeout(tick, delay)
      delay = Math.min(delay * 2, 15_000)
    }
    timer = setTimeout(tick, 600)
    const onOnline = () => {
      delay = 3000
      clearTimeout(timer)
      void tick()
    }
    window.addEventListener('online', onOnline)
    return () => {
      cancelled = true
      clearTimeout(timer)
      window.removeEventListener('online', onOnline)
    }
  }, [standingBy, recover])

  // Manual retry probes first too, so it can never reload or refetch into a still-dead network.
  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const retry = React.useCallback(() => {
    setStandingBy(false)
    void serverReachable().then((ok) => {
      setReachable(ok)
      if (ok) recover(true)
    })
  }, [recover])

  const offline = reachable === false
  return (
    <div className="flex min-h-[40dvh] w-full flex-col items-center justify-center gap-3 p-8 text-center">
      {!standingBy && (
        <div
          className="border-muted-foreground/30 border-t-foreground size-6 animate-spin rounded-full border-2"
          aria-hidden
        />
      )}
      <p className="text-foreground text-base font-semibold">
        {offline ? t`No internet connection` : standingBy ? t`Something went wrong` : t`Reconnecting…`}
      </p>
      <p className="text-muted-foreground max-w-sm text-sm">
        {offline
          ? t`We’ll load this page automatically as soon as you’re back online.`
          : standingBy
            ? t`We couldn’t load this page. Please try again.`
            : t`This is taking longer than usual. We’ll keep trying to load this page.`}
      </p>
      <ButtonPrimitive
        type="button"
        onClick={retry}
        className={cn('bg-primary text-primary-foreground mt-1 rounded-md px-4 py-2 text-sm font-medium')}
      >
        {t`Try again`}
      </ButtonPrimitive>
    </div>
  )
}
